import { useIonRouter, UseIonRouterResult } from "@ionic/react";
import { ActivityLog } from "components/Molecules/ActivityLog/ActivityLog";
import { SubInfoCard } from "components/Organisms/SubInfoCard/SubInfoCard";
import { SubWaitlistSelectionView } from "components/Organisms/SubWaitlistSelectionView/SubWaitlistSelectionView";
import { RequestModel } from "model";
import { createSubProfilePath } from "routes/routeData";
import { Card, Colors } from "swing-components";
import { useIsDesktop, useShouldRestrictAdmin } from "utils/hooks";
import { RESTRICTIONS } from "utils/security-restrictions";

import { RequestDetailsCard } from "~components/RequestDetailsCard/RequestDetailsCard";
import { RequestInfoCard } from "~components/RequestInfoCard/RequestInfoCard";

type RequestDetailsViewProps = {
  request: RequestModel;
};

export function RequestDetailsView(props: RequestDetailsViewProps) {
  const { request } = props;

  /********* Hooks ********/
  const router = useIonRouter();
  const isDesktop = useIsDesktop();

  const isViewOnly = useShouldRestrictAdmin([
    RESTRICTIONS.VIEW_ONLY_ADMIN,
    RESTRICTIONS.CLASSROOM_VIEW_ONLY,
  ]);

  /********* Render ********/
  return (
    <div
      css={{
        padding: "24px 16px 0",
        "@media (min-width: 900px)": {
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: "0 24px",
          padding: "24px 24px 0",
        },
      }}
    >
      {isDesktop ? (
        <DesktopLayout request={request} isViewOnly={isViewOnly} router={router} />
      ) : (
        <MobileLayout request={request} isViewOnly={isViewOnly} router={router} />
      )}

      {/* TODO: Should we show "No Activity" when there is 0 activity? */}
      <ActivityLog request={request} />
    </div>
  );
}

/********* Helper Components ********/
type RequestDetailsSubInfoCardProps = {
  request: RequestModel;
  router: UseIonRouterResult;
};

function RequestDetailsSubInfoCard(props: RequestDetailsSubInfoCardProps) {
  const { request, router } = props;

  if (request.isFilled() || request.isCompleted()) {
    return (
      <SubInfoCard
        school={request.school}
        status={request.isCompleted() ? "COMPLETED" : "FILLED"}
        intervals={request.getIntervals(true)}
        requestId={request.id}
        sub={{
          id: request.sub.id,
          email: request.sub.email,
          name: request.sub.name,
          onSubNameClick: () =>
            router.push(createSubProfilePath("active-requests", request.sub.id)),
          phoneNumber: request.sub.phoneNumber,
          profileImageUrl: request.sub.photo,
          isSwingHero: request.sub.isSwingHero,
        }}
        display="empty"
        hasFeedback={request.hasFeedback}
        feedback={request.feedback}
        subLastFeedback={request.subLastFeedback}
      />
    );
  }

  if (request.isCanceled()) {
    return (
      <SubInfoCard
        status="CANCELLED"
        school={request.school}
        cancellation={request?.cancellation}
        sub={{
          id: request?.sub?.id,
          name: request?.sub?.name,
        }}
        requestId={request.id}
        intervals={request.getIntervals(true)}
        hasFeedback={request.hasFeedback}
        feedback={request.feedback}
        subLastFeedback={request.subLastFeedback}
      />
    );
  }

  if (request.isConfirmationPending()) {
    // waiting - admin has requested a sub who has a conflict
    return (
      <SubInfoCard
        status="PENDING"
        sub={{
          name: request.pendingSub.name,
          onSubNameClick: () =>
            router.push(createSubProfilePath("active-requests", request.pendingSub.id)),
          profileImageUrl: request.pendingSub.photo,
        }}
      />
    );
  }

  if (request?.isClosed()) {
    return <SubInfoCard status="CLOSED" />;
  }

  if (request?.isWaitlistReq()) {
    // sub signed up for long term request and waiting for admin to choose a sub
    return <SubWaitlistSelectionView request={request} />;
  }

  return <SubInfoCard status="UNFILLED" />;
}

type LayoutProps = {
  request: RequestModel;
  isViewOnly: boolean;
  router: UseIonRouterResult;
};

function DesktopLayout(props: LayoutProps) {
  const { request, isViewOnly, router } = props;

  return (
    <>
      <Card>
        <RequestInfoCard canModify={!isViewOnly} request={request} />
        <div css={{ height: 1, backgroundColor: Colors.slate200 }} />
        <RequestDetailsCard request={request} />
      </Card>

      <div>
        <RequestDetailsSubInfoCard request={request} router={router} />
      </div>
    </>
  );
}

function MobileLayout(props: LayoutProps) {
  const { request, isViewOnly, router } = props;
  const isFilledOrCompleted = request.isFilled() || request.isCompleted();
  const isWaitlisted = request.poolCount >= 1 && !request.isCanceled();

  const showSubCardAbove = isFilledOrCompleted || isWaitlisted;

  return (
    <>
      {/* SubInfoCard ABOVE Request Details */}
      {showSubCardAbove && (
        <>
          <Card>
            <RequestInfoCard canModify={!isViewOnly} request={request} />
          </Card>

          <div css={{ margin: "24px 0" }}>
            <RequestDetailsSubInfoCard request={request} router={router} />
          </div>

          <Card>
            <RequestDetailsCard request={request} />
          </Card>
        </>
      )}

      {/* SubInfoCard BELOW Request Details */}
      {!showSubCardAbove && (
        <>
          <Card>
            <RequestInfoCard canModify={!isViewOnly} request={request} />
            <div css={{ height: 1, backgroundColor: Colors.slate200 }} />
            <RequestDetailsCard request={request} />
          </Card>

          <div css={{ margin: "24px 0" }}>
            <RequestDetailsSubInfoCard request={request} router={router} />
          </div>
        </>
      )}
    </>
  );
}
